import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Play, Trash2, ClipboardPaste, ExternalLink } from "lucide-react";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const TYPES = [
  { v: "instagram", label: "Instagram" },
  { v: "facebook_page", label: "Facebook Page" },
  { v: "facebook_group", label: "Facebook Group" },
  { v: "facebook_event", label: "Facebook Events (search)" },
  { v: "eventbrite", label: "Eventbrite" },
  { v: "google_events", label: "Google Events" },
  { v: "website", label: "Website (generic URL fetch)" },
  { v: "newsletter", label: "Newsletter (IMAP)" },
];

const CONFIG_HINTS = {
  instagram: '{"handles": ["portlandmaine"], "max_posts": 15}',
  facebook_page: '{"urls": ["https://www.facebook.com/PortlandME"], "max_posts": 15}',
  facebook_group: '{"urls": ["https://www.facebook.com/groups/1234567890"], "max_posts": 15}',
  facebook_event: '{"search_queries": ["Portland Maine events this weekend"], "max_events": 40}',
  eventbrite: '{"location": "Portland, Maine", "when": "this-weekend", "max_events": 40}',
  google_events: '{"queries": ["events this weekend in Portland Maine"], "location": "Portland, Maine", "max_per_query": 10}',
  website: '{"urls": ["https://www.statetheatreportland.com/events"]}',
  newsletter: '{"host": "imap.gmail.com", "port": 993, "email": "events@…", "password": "app-password", "mailbox": "INBOX"}',
};

export default function AdminSources() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [form, setForm] = useState({ name: "", type: "instagram", active: true, config: CONFIG_HINTS.instagram });
  const [running, setRunning] = useState({});

  const load = useCallback(async () => setItems((await api.get("/admin/sources")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const cfg = JSON.parse(form.config || "{}");
      await api.post("/admin/sources", { name: form.name, type: form.type, active: form.active, config: cfg });
      toast.success("Source added"); setOpen(false);
      setForm({ name: "", type: "instagram", active: true, config: CONFIG_HINTS.instagram });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  const toggle = async (s, active) => {
    await api.patch(`/admin/sources/${s.id}`, { name: s.name, type: s.type, active, config: s.config });
    load();
  };
  const run = async (s) => {
    setRunning({ ...running, [s.id]: true });
    try {
      const r = await api.post(`/admin/sources/${s.id}/run`);
      if (r.data.status === "queued") {
        toast.success(`Run queued for "${r.data.source_name}" — check Sync logs in a minute.`);
      } else if (r.data.status === "error") {
        toast.error(`Error: ${r.data.message}`);
      } else {
        toast.success(`Fetched ${r.data.raw_count ?? 0} · created ${r.data.events_created ?? 0} · deduped ${r.data.events_deduped ?? 0}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      // Give the background job a moment to update source.last_run before re-enabling
      setTimeout(() => setRunning((prev) => ({ ...prev, [s.id]: false })), 2000);
    }
  };
  const del = async (id) => { if (!confirm("Delete source?")) return; await api.delete(`/admin/sources/${id}`); load(); };

  const runAll = async () => {
    if (!confirm("Kick off a full scrape of every active source right now? This can take 30+ minutes and burns Apify credits.")) return;
    try {
      const r = await api.post("/admin/sources/run-all");
      toast.success(`Queued ${r.data.sources_queued} sources — track progress in Sync logs.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run-all failed");
    }
  };

  // ---- Bulk add ----
  const openBulk = () => {
    setBulkText(""); setBulkPreview(null); setBulkOpen(true);
  };
  const runBulkPreview = async () => {
    if (!bulkText.trim()) return;
    setBulkLoading(true);
    try {
      const r = await api.post("/admin/sources/bulk-preview", { text: bulkText });
      setBulkPreview(r.data.items || []);
    } catch (e) {
      toast.error("Preview failed");
    } finally { setBulkLoading(false); }
  };
  const commitBulk = async () => {
    setBulkLoading(true);
    try {
      const r = await api.post("/admin/sources/bulk-add", { text: bulkText });
      const { created, skipped, errors } = r.data;
      const bits = [];
      if (created?.length) bits.push(`${created.length} added`);
      if (skipped?.length) bits.push(`${skipped.length} skipped (dup)`);
      if (errors?.length) bits.push(`${errors.length} rejected`);
      toast.success(bits.join(" · ") || "Nothing to add");
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    } finally { setBulkLoading(false); }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#1A3C5A]">Sources</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runAll} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="run-all-btn">
            <Play size={14} className="mr-1"/>Run all now
          </Button>
          <Button variant="outline" onClick={openBulk} className="border-[#1A3C5A] text-[#1A3C5A] hover:bg-slate-50" data-testid="bulk-add-btn">
            <ClipboardPaste size={14} className="mr-1"/>Bulk add URLs
          </Button>
          <Button onClick={()=>setOpen(true)} className="bg-[#1A3C5A] text-white hover:bg-[#12293e]" data-testid="new-source-btn">
            <Plus size={14} className="mr-1"/>New source
          </Button>
        </div>
      </div>
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="py-2 px-2 text-left">Name</th>
              <th className="py-2 px-2 text-left">Type</th>
              <th className="py-2 px-2 text-left">Last run</th>
              <th className="py-2 px-2 text-right">Raw</th>
              <th className="py-2 px-2 text-right">Events</th>
              <th className="py-2 px-2">Active</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-border" data-testid={`source-row-${s.id}`}>
                <td className="py-1.5 px-2 font-medium">{s.name}</td>
                <td className="py-1.5 px-2 text-slate-600">{s.type}</td>
                <td className="py-1.5 px-2 text-xs text-slate-500">
                  {s.last_run ? (
                    <><div>{(() => { try { return formatInTimeZone(parseISO(s.last_run), "America/New_York", "MMM d, HH:mm"); } catch { return s.last_run; } })()}</div>
                      <div className={s.last_status === "error" ? "text-red-600" : "text-emerald-600"}>{s.last_status}</div></>
                  ) : "never"}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums">{s.total_raw || 0}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{s.total_events || 0}</td>
                <td className="py-1.5 px-2 text-center">
                  <Switch checked={s.active} onCheckedChange={(v)=>toggle(s, v)} />
                </td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>run(s)} disabled={!!running[s.id]} data-testid={`run-${s.id}`}
                          className="text-[#1A3C5A]">
                    <Play size={13}/> {running[s.id] ? "…" : "Run"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(s.id)} className="text-red-600"><Trash2 size={13}/></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-500">No sources yet. Add one to start ingesting.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-source-dialog">
          <DialogHeader><DialogTitle className="font-display">New source</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="src-name"/>
            </div>
            <div className="space-y-1"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v, config: CONFIG_HINTS[v]})}>
                <SelectTrigger data-testid="src-type"><SelectValue/></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Config (JSON)</Label>
              <Textarea value={form.config} onChange={(e)=>setForm({...form, config: e.target.value})} rows={5} className="font-mono text-xs" data-testid="src-config"/>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v)=>setForm({...form, active: v})} /><Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1A3C5A] text-white hover:bg-[#12293e]" data-testid="src-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk add source URLs</DialogTitle>
            <DialogDescription className="text-slate-500">
              Paste one URL per line (or comma-separated). I&apos;ll auto-detect the type:
              <br/>
              <span className="text-xs text-slate-400">
                facebook.com/groups/… → FB Group · facebook.com/events/… → FB Event · facebook.com/… → FB Page ·
                instagram.com/… or <code>@handle</code> → Instagram · eventbrite.com → Eventbrite · anything else → Website
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              rows={7}
              value={bulkText}
              onChange={(e) => { setBulkText(e.target.value); setBulkPreview(null); }}
              placeholder={"https://www.facebook.com/groups/portlandblue\nhttps://www.instagram.com/portlandmaine/\nhttps://www.statetheatreportland.com/events\n@spaceportland"}
              className="font-mono text-xs"
              data-testid="bulk-add-textarea"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={runBulkPreview} disabled={bulkLoading || !bulkText.trim()} data-testid="bulk-preview-btn">
                {bulkLoading ? "…" : "Preview"}
              </Button>
              <div className="ml-auto text-xs text-slate-500 self-center">
                {bulkPreview && `${bulkPreview.filter(x => !x.error).length} recognized · ${bulkPreview.filter(x => x.error).length} rejected`}
              </div>
            </div>

            {bulkPreview && (
              <div className="border border-border rounded-md bg-slate-50 max-h-64 overflow-y-auto text-sm" data-testid="bulk-preview-list">
                {bulkPreview.map((it) => (
                  <div key={it.canonical_url || it.input} className="border-b border-border last:border-0 px-2 py-1.5 flex items-center gap-2">
                    {it.error ? (
                      <>
                        <Badge variant="outline" className="border-red-300 text-red-600 text-[10px]">reject</Badge>
                        <span className="text-slate-400 truncate flex-1">{it.input}</span>
                        <span className="text-xs text-red-500">{it.error}</span>
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="border-emerald-500 text-emerald-700 text-[10px]">{it.type}</Badge>
                        <span className="truncate flex-1 font-medium">{it.name}</span>
                        <a href={it.canonical_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#1A3C5A]">
                          <ExternalLink size={11}/>
                        </a>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              onClick={commitBulk}
              disabled={bulkLoading || !bulkText.trim()}
              className="bg-[#1A3C5A] text-white hover:bg-[#12293e]"
              data-testid="bulk-commit-btn"
            >
              {bulkLoading ? "Adding…" : "Add all recognized"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
